/**
 * Suivi des pages vues GA4 dans une SPA.
 *
 * ⚠️ Sans ce hook, seul le `gtag('config')` de index.html envoie un page_view,
 * et uniquement au chargement initial : toute la navigation interne (wouter ne
 * recharge pas la page) est invisible dans GA4. C'est ce qui faisait remonter
 * ~97 % des vues sur « / » — un artefact de mesure, pas un comportement réel.
 */

import { useEffect, useRef } from "react";
import { useLocation } from "wouter";

const GA_MEASUREMENT_ID = "G-B50QB7GW3Q";

// Même clé et même règle de consentement que useAnalytics.ts
function hasAnalyticsConsent(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const stored = localStorage.getItem("veastudio-cookie-consent");
    if (!stored) return false;
    return JSON.parse(stored).analytics === true;
  } catch {
    return false;
  }
}

export function usePageTracking() {
  const [location] = useLocation();
  // Le page_view initial est déjà envoyé par gtag('config') dans index.html :
  // on ignore la première valeur pour ne pas le compter deux fois.
  const initialLocation = useRef(location);
  const isFirstRun = useRef(true);

  useEffect(() => {
    if (isFirstRun.current) {
      isFirstRun.current = false;
      if (location === initialLocation.current) return;
    }

    if (typeof window === "undefined" || typeof window.gtag !== "function") return;
    if (!hasAnalyticsConsent()) return;

    window.gtag("event", "page_view", {
      send_to: GA_MEASUREMENT_ID,
      page_path: location,
      page_location: window.location.href,
      page_title: document.title,
    });
  }, [location]);
}
